/**
 * Settings → Bekijken als: which half of the page is open (in the URL), and
 * the people table's filters and sort, as plain functions.
 */

import type { ViewAsPerson } from "@/lib/api";
import { fullName } from "@/lib/navigation";
import { ROLES } from "@/lib/roles";
import { matchesSearch } from "@/lib/search";
import { type SortColumns, sortTime, type SortValue, type TableSort } from "@/lib/table-sort";

/** What the page offers to view the dashboard as: a person, or a role. */
export type ViewAsChoice = "person" | "role";

/** The URL's parameter for the choice, `?as=rol`; a person without it. */
export const CHOICE_PARAM = "as";

/** The parameter's value for a role. */
const ROLE_VALUE = "rol";

/** The choice the URL holds: a role with `?as=rol`, else a person. */
export function viewAsChoice(params: URLSearchParams): ViewAsChoice {
  return params.get(CHOICE_PARAM) === ROLE_VALUE ? "role" : "person";
}

/** The URL's parameters with `choice` in them, the others kept; a person leaves the parameter out. */
export function withChoice(params: URLSearchParams, choice: ViewAsChoice): URLSearchParams {
  const next = new URLSearchParams(params);
  if (choice === "role") {
    next.set(CHOICE_PARAM, ROLE_VALUE);
  } else {
    next.delete(CHOICE_PARAM);
  }
  return next;
}

/** The people table's filters: a search on name and email, and roles (none for every role). */
export interface ViewAsFilters {
  search: string;
  roles: string[];
}

export const NO_VIEW_AS_FILTERS: ViewAsFilters = { search: "", roles: [] };

/** Whether any filter is set: the filter row then offers to reset them. */
export function hasViewAsFilters(filters: ViewAsFilters): boolean {
  return filters.search.trim() !== "" || filters.roles.length > 0;
}

/** The people that match: every word of the search in their name or email, and a chosen role. */
export function filterViewAsPeople(
  people: readonly ViewAsPerson[],
  filters: ViewAsFilters,
): ViewAsPerson[] {
  return people.filter(
    (person) =>
      (filters.roles.length === 0 || filters.roles.includes(person.role)) &&
      matchesSearch([fullName(person), person.email], filters.search),
  );
}

/** The people table's sortable columns. */
export type ViewAsColumn = "name" | "role" | "teams" | "last";

/** Each column's first order: names and teams from A, roles from Lid up, the latest activity first. */
export const VIEW_AS_SORT_COLUMNS: SortColumns<ViewAsColumn> = {
  name: "asc",
  role: "asc",
  teams: "asc",
  last: "desc",
};

/** The order the table opens in, as its header shows: by name. */
export const VIEW_AS_SORT_INITIAL: TableSort<ViewAsColumn> = { key: "name", dir: "asc" };

/**
 * What a person sorts on in a column: their name (else their email), their
 * role's rank, their teams' names as shown (none goes last), their latest
 * call (none goes last).
 */
export function viewAsSortValue(person: ViewAsPerson, column: ViewAsColumn): SortValue {
  switch (column) {
    case "name":
      return fullName(person) ?? person.email;
    case "role": {
      const rank = (ROLES as readonly string[]).indexOf(person.role);
      return rank === -1 ? null : rank;
    }
    case "teams":
      return person.teams.length > 0 ? person.teams.join(", ") : null;
    case "last":
      return sortTime(person.last_active_at);
  }
}
